import React from 'react';
import { useLocation } from 'react-router-dom';
import { PROJECT_SETTINGS_PATH } from '@/components/analytics-integrations/amplitudeSettingsLink';
import { Button, Notice } from '@/components/kit/ds';
import { useAppSelector } from '@/hooks/hooks';
import { useOrgTopUp } from '@/hooks/useOrgTopUp';
import type { Project } from '@/types';
import { useStrings } from '@/i18n';
import { billingStrings } from '@/i18n/dictionaries/billing';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import '../../styles/cabinet-shell.css';

/**
 * ProjectFrozenBanner — WEB-754 (доделка аудита 22.08, п.1).
 *
 * Проект, по которому покупатель оспорил платёж в банке, переводится в статус
 * «заморожен»: сторож статуса закрывает ВСЕ проектные ручки кабинета
 * (`backend/src/projects/guards/status.guard.ts:55-60`, код `PROJECT_FROZEN`),
 * а публичные страницы проекта перестают открываться у посетителей. До этой
 * плашки кабинет об этом молчал — у человека одновременно вставал бизнес и
 * ломался кабинет, а продукт не говорил ни что случилось, ни куда идти.
 *
 * ИСТОЧНИК ПРИЗНАКА — статус самого проекта (`project.status === 'frozen'`),
 * который сервер отдаёт в списке проектов и в карточке проекта
 * (`projects.service.ts:404/475`; описание поля в ProjectDto прямо говорит
 * «FE renders frozen banner» — §F13). Признак `frozen` из ответа `GET /billing`
 * СОЗНАТЕЛЬНО не используется: его считают по проекту, который сервер
 * ВЫБРАЛ САМ (`resolvedProjectId`), а кабинет зовёт `fetchBillingThunk` без
 * projectId — то есть плашка могла бы утверждать заморозку не того проекта,
 * который человек сейчас видит. Утверждать то, чего мы не знаем, на денежном
 * пути нельзя (класс WEB-1528).
 *
 * Оформление (WEB-3739 юнит 3, эталон `design/cabinet-rescin/app.jsx`
 * `.frz-shell`, «плашки везде одного вида» — владелец 23.10): `Notice` кита вида
 * `error` в своём корне `.ds` (тема человека) — заголовок «Проект … приостановлен»,
 * описание — причина (у кого нет права — просьба к владельцу), кнопка «Баланс» —
 * действием справа. Стили `.frz-shell` — `styles/cabinet-shell.css`: над
 * страницей; в конструкторе (`main.full`) — плавает над строкой чата.
 */
export default function ProjectFrozenBanner() {
  const s = useStrings(billingStrings);
  const user = useAppSelector((state) => state.auth?.user);
  const root = useCabinetRootAttributes(user?.theme);
  const currentProject = useAppSelector(
    (state) => state.projects?.currentProject ?? null,
  );
  const projects = useAppSelector((state) => state.projects?.projects ?? []);
  // Компонент живёт в каркасе кабинета и рисуется на КАЖДОМ экране: если срез
  // проектов ещё не готов (или подменён в тесте оболочки), он обязан молча
  // ничего не показать, а не уронить весь кабинет исключением.
  const projectList: Project[] = Array.isArray(projects) ? projects : [];
  // Выбран проект — говорим только о НЁМ. В режиме организации проекта нет,
  // и человек всё равно должен узнать, что в организации есть замороженный
  // проект (иначе плашка исчезала бы ровно там, где кабинет и так пустой).
  const frozen: Project[] = currentProject
    ? currentProject.status === 'frozen'
      ? [currentProject]
      : []
    : projectList.filter((p) => p.status === 'frozen');

  // WEB-1126/WEB-1246 (как в PastDueBanner): страница «Баланс» закрыта для
  // рядового участника ролью — вести его туда значило бы заменить одно
  // молчание другим тупиком. role === null («список организаций ещё не
  // загружен») трактуем как «показать кнопку»: прятать её у настоящего
  // владельца нельзя, страница сама объяснит отказ, если прав не хватит.
  //
  // WEB-1417 (ревизия пачки 6): плашка ПРОЕКТНАЯ, поэтому роль читается в
  // организации ЭТОГО проекта, а не в активной. У человека в двух
  // организациях активная могла быть чужой по правам, и ветка решалась по
  // ней: настоящий владелец видел тупик «обратитесь к владельцу», а рядовой
  // участник — кнопку в закрытый ему раздел.
  //
  // Замороженных несколько (режим организации) — адресовать вопрос одной
  // организации нечему, и выдумывать выбор нельзя: остаётся прежнее
  // поведение по активной организации.
  //
  // WEB-2952: право и переход — ПАРОЙ из двери useOrgTopUp. Раньше роль
  // судила организация проекта, а кнопка вела на «Баланс» — страницу АКТИВНОЙ
  // организации: владелец организации Б при активной А уходил пополнять
  // кошелёк А. Теперь дверь сперва переключает организацию на организацию
  // проекта (сервер отказал — на чужой баланс не ведём). Признак права — тот
  // же canManageOrgBilling, которым сервер судит денежные пути (раньше —
  // «владелец или администратор»: администратор, которому владелец закрыл
  // деньги, получал кнопку в отказ).
  //
  // WEB-3327: кнопка здесь ведёт не к покупке пакетов, а в «Баланс» —
  // разбираться со спорным платежом. Поэтому судит право (`canSettle`), а не
  // `canTopUp` двери: тот с WEB-3327 ложен у владельца, пока продажа пакетов
  // выключена, и владелец читал бы «обратитесь к владельцу».
  const topUp = useOrgTopUp(frozen.length === 1 ? frozen[0].orgId : null);
  // WEB-3888 (решение оркестратора 24.10, владелец 24.10 п. 4б): на настройках
  // проекта своя плашка `sk-frozen` (components/settings/ds/FrozenNotice) над
  // погашенными блоками — вторая плашка об одном и том же там не рисуется.
  const { pathname } = useLocation();
  const onProjectSettings =
    pathname.replace(/\/+$/, '') === PROJECT_SETTINGS_PATH;

  if (frozen.length === 0 || onProjectSettings) {
    return null;
  }

  const names = frozen.map((p) => s.quotedName(p.name)).join(', ');
  const subject =
    frozen.length > 1
      ? s.frozenProjectsSubject(names)
      : s.frozenProjectSubject(names);

  return (
    <div
      {...root}
      className={`${root.className} frz-shell`}
      data-testid="project-frozen-banner"
    >
      <Notice
        kind="error"
        testid="project-frozen-notice"
        title={subject}
        action={
          topUp.canSettle ? (
            <Button
              size="sm"
              kind="primary"
              onClick={() => {
                void topUp.goToBalance();
              }}
              data-testid="project-frozen-billing-btn"
            >
              {s.frozenGoToBalance}
            </Button>
          ) : null
        }
      >
        {s.frozenReason}
        {!topUp.canSettle && (
          <>
            {' '}
            <span data-testid="project-frozen-member-note">
              {s.frozenMemberNote}
            </span>
          </>
        )}
      </Notice>
    </div>
  );
}
